// src/tabs/logements/LivretCopyFromModal.jsx — L3 « Récupérer depuis [logement] »
//
// Modal de copie de modules OU FAQ depuis un autre logement vers le logement
// courant. Mode AJOUT pur (serveur INSERT id neuf — jamais d'écrasement).
// v1 SANS photos : badge « photos non copiées » sur les modules concernés.
//
// Props :
//   - mode : "modules" | "faqs"
//   - targetLogementId : logement courant (destination)
//   - candidates : [{ id, nom }] logements source possibles (courant exclu)
//   - onClose() / onCopied() : callbacks (onCopied → le parent recharge)

import { useState, useCallback } from "react";
import { DS } from "../../lib/ui";
import { Download, Camera, AlertTriangle } from "lucide-react";
import { supabase } from "../../supabase";

import useEchapPourFermer from "../../lib/useEchapPourFermer"
async function getAuthHeaders() {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  const token = session?.access_token;
  return token
    ? { "Content-Type": "application/json", Authorization: `Bearer ${token}` }
    : { "Content-Type": "application/json" };
}

const overlayStyle = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.5)",
  zIndex: 1000,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 20,
};

export default function LivretCopyFromModal({
  mode,
  targetLogementId,
  candidates,
  onClose,
  onCopied,
}) {
  // Échap ferme — consigne 2026-08-14, hook partagé (pile anti-double-fermeture).
  useEchapPourFermer(onClose)

  const isModules = mode === "modules";
  const [sourceId, setSourceId] = useState("");
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]); // modules[] ou faqs[]
  const [photosByModule, setPhotosByModule] = useState({});
  const [selected, setSelected] = useState({}); // { id: true }
  const [copying, setCopying] = useState(false);
  const [error, setError] = useState(null);

  const loadSource = useCallback(
    async (sid) => {
      setSourceId(sid);
      setItems([]);
      setSelected({});
      setError(null);
      if (!sid) return;
      setLoading(true);
      try {
        const headers = await getAuthHeaders();
        const resp = await fetch(
          `/api/beds24-mapping?action=livret_admin_data&logement_id=${encodeURIComponent(sid)}`,
          { method: "GET", headers },
        );
        const json = await resp.json();
        if (!resp.ok)
          throw new Error(json.message || json.error || `HTTP ${resp.status}`);
        setItems(isModules ? json.modules || [] : json.faqs || []);
        setPhotosByModule(json.module_photos || {});
      } catch (e) {
        setError(e.message || String(e));
      } finally {
        setLoading(false);
      }
    },
    [isModules],
  );

  function toggle(id) {
    setSelected((p) => ({ ...p, [id]: !p[id] }));
  }

  const selectedIds = Object.keys(selected).filter((k) => selected[k]);

  async function handleCopy() {
    if (copying || selectedIds.length === 0) return;
    setCopying(true);
    setError(null);
    try {
      const headers = await getAuthHeaders();
      const body = {
        action: "livret_copy_items",
        source_logement_id: sourceId,
        target_logement_id: targetLogementId,
        module_ids: isModules ? selectedIds : [],
        faq_ids: isModules ? [] : selectedIds,
      };
      const resp = await fetch(
        "/api/beds24-mapping?action=livret_copy_items",
        { method: "POST", headers, body: JSON.stringify(body) },
      );
      const json = await resp.json();
      if (!resp.ok)
        throw new Error(json.message || json.error || `HTTP ${resp.status}`);
      onCopied?.(json);
      onClose?.();
    } catch (e) {
      setError(e.message || String(e));
      setCopying(false);
    }
  }

  function moduleHasPhotos(m) {
    return (
      (photosByModule[m.id] && photosByModule[m.id].length > 0) ||
      (Array.isArray(m.photo_urls) && m.photo_urls.length > 0)
    );
  }

  return (
    <div style={overlayStyle} onClick={onClose} role="dialog" aria-modal="true">
      <div
        onClick={(e) => e.stopPropagation()}
        className="fbz-modal-sheet"
        style={{
          background: DS.card,
          borderRadius: 14,
          padding: 22,
          width: "min(560px, 94vw)",
          maxHeight: "86vh",
          overflowY: "auto",
          fontFamily: DS.fontBody,
          boxShadow: "0 12px 40px rgba(0,0,0,0.3)",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 9,
            fontFamily: DS.fontDisplay,
            fontSize: 17,
            fontWeight: 600,
            color: DS.marineDark,
            marginBottom: 4,
          }}
        >
          <span style={{
            display: "inline-flex", width: 28, height: 28, borderRadius: 8,
            background: DS.catCielBg, color: DS.catCielIco, alignItems: "center", justifyContent: "center",
          }}>
            <Download size={16} strokeWidth={1.8} />
          </span>
          Récupérer depuis un autre logement
        </div>
        <div style={{ fontSize: 12, color: DS.textSecondary, marginBottom: 16, marginLeft: 37 }}>
          Copie {isModules ? "des modules" : "des FAQ"} d'un autre logement vers
          celui-ci (ajout — n'écrase rien).
        </div>

        {error && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 9,
              padding: 10,
              background: DS.dangerBg,
              border: `0.5px solid ${DS.danger}`,
              borderRadius: 8,
              color: DS.dangerText,
              fontSize: 12,
              marginBottom: 12,
            }}
          >
            <AlertTriangle size={15} strokeWidth={1.8} style={{ flex: "0 0 auto" }} /> {error}
          </div>
        )}

        {/* Sélecteur logement source */}
        <div style={{ marginBottom: 14 }}>
          <div style={lbl}>Logement source</div>
          <select
            value={sourceId}
            onChange={(e) => loadSource(e.target.value)}
            disabled={copying}
            style={{ ...inpStyle, cursor: "pointer" }}
          >
            <option value="">— Choisir un logement —</option>
            {candidates.map((c) => (
              <option key={c.id} value={c.id}>
                {c.id} · {c.nom}
              </option>
            ))}
          </select>
        </div>

        {/* Liste des items */}
        {loading && (
          <div style={{ fontSize: 12, color: "#888", fontStyle: "italic" }}>
            Chargement…
          </div>
        )}
        {!loading && sourceId && items.length === 0 && (
          <div style={{ fontSize: 12, color: "#888", fontStyle: "italic" }}>
            Aucun {isModules ? "module" : "FAQ"} dans ce logement.
          </div>
        )}
        {!loading && items.length > 0 && (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 6,
              marginBottom: 16,
            }}
          >
            {items.map((it) => {
              const label = isModules
                ? it.title?.fr || it.title?.en || "(sans titre)"
                : it.question?.fr || it.question?.en || "(sans question)";
              const withPhotos = isModules && moduleHasPhotos(it);
              return (
                <label
                  key={it.id}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    padding: "8px 10px",
                    border: `0.5px solid ${selected[it.id] ? DS.ciel : DS.border}`,
                    borderRadius: 8,
                    background: selected[it.id] ? DS.cielSoft : DS.card,
                    cursor: "pointer",
                    fontSize: 13,
                  }}
                >
                  <input
                    type="checkbox"
                    checked={!!selected[it.id]}
                    onChange={() => toggle(it.id)}
                    style={{ accentColor: DS.navy }}
                  />
                  <span
                    style={{
                      flex: 1,
                      minWidth: 0,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      color: DS.marineDark,
                    }}
                  >
                    {label}
                  </span>
                  {isModules && it.category && (
                    <span style={{ fontSize: 10, color: DS.textMuted }}>
                      {it.category}
                    </span>
                  )}
                  {withPhotos && (
                    <span
                      title="Les photos ne sont pas copiées (v1)"
                      style={{
                        flexShrink: 0,
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 4,
                        fontSize: 9,
                        padding: "2px 6px",
                        background: DS.catCorailBg,
                        color: DS.catCorailIco,
                        borderRadius: 4,
                        fontWeight: 700,
                      }}
                    >
                      <Camera size={11} strokeWidth={1.8} /> photos non copiées
                    </span>
                  )}
                </label>
              );
            })}
          </div>
        )}

        {/* Actions */}
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 8,
            marginTop: 8,
          }}
        >
          <button
            type="button"
            onClick={onClose}
            disabled={copying}
            className="fbz-press"
            style={{
              padding: "9px 16px",
              background: DS.card,
              color: DS.textSecondary,
              border: `0.5px solid ${DS.borderHover}`,
              borderRadius: DS.radiusControl,
              fontSize: 12,
              fontWeight: 600,
              cursor: copying ? "not-allowed" : "pointer",
              fontFamily: DS.fontBody,
            }}
          >
            Annuler
          </button>
          <button
            type="button"
            onClick={handleCopy}
            disabled={copying || selectedIds.length === 0}
            className="fbz-press"
            style={{
              padding: "9px 16px",
              background:
                copying || selectedIds.length === 0 ? "#cbd5e1" : DS.corail,
              color: "#fff",
              border: "none",
              borderRadius: DS.radiusControl,
              fontSize: 12,
              fontWeight: 600,
              cursor:
                copying || selectedIds.length === 0 ? "not-allowed" : "pointer",
              fontFamily: DS.fontBody,
            }}
          >
            {copying
              ? "Copie…"
              : `Copier (${selectedIds.length}) vers ce logement`}
          </button>
        </div>
      </div>
    </div>
  );
}

const lbl = {
  fontSize: 11,
  color: "#888",
  fontWeight: 700,
  marginBottom: 5,
  textTransform: "uppercase",
  letterSpacing: 0.4,
};

const inpStyle = {
  border: "1px solid #dde3ec",
  borderRadius: 7,
  padding: "8px 12px",
  fontSize: 13,
  outline: "none",
  background: "#f8fafd",
  fontFamily: DS.fontBody,
  width: "100%",
  boxSizing: "border-box",
};
